import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  Pricelist,
  Client,
  Project,
  pricelistsAPI,
  clientsAPI,
  projectsAPI,
} from '../../services/api';
import PricelistDetailView from './PricelistDetailView';
import { DollarSignIcon, SearchAltIcon } from '../icons';

const Icons = {
  pricelist: <DollarSignIcon className="w-7 h-7" strokeWidth={1.5} />,
  search: <SearchAltIcon className="w-5 h-5" strokeWidth={2} />,
};

const PricelistManagement: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { confirm } = useConfirm();
  const [pricelists, setPricelists] = useState<Pricelist[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPricelist, setEditingPricelist] = useState<Pricelist | null>(
    null,
  );
  const [selectedPricelistId, setSelectedPricelistId] = useState<number | null>(
    null,
  );
  const [isNewPricelist, setIsNewPricelist] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    project_id: '',
    billable_rate: '',
    cost_rate: '',
    currency: 'CAD' as 'CAD' | 'USD',
    effective_date: new Date().toISOString().split('T')[0],
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pricelistsResponse, clientsResponse, projectsResponse] =
        await Promise.all([
          pricelistsAPI.getAll(),
          clientsAPI.getAll(),
          projectsAPI.getAll(),
        ]);

      setPricelists(pricelistsResponse.data.pricelists || []);
      setClients(clientsResponse.data.clients || []);
      setProjects(projectsResponse.data.projects || []);
    } catch (error: any) {
      setError(error.response?.data?.error || t('pricelists.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      client_id: '',
      project_id: '',
      billable_rate: '',
      cost_rate: '',
      currency: 'CAD',
      effective_date: new Date().toISOString().split('T')[0],
    });
    setEditingPricelist(null);
    setShowForm(false);
  };

  const handleEdit = (pricelist: Pricelist) => {
    setEditingPricelist(pricelist);
    setFormData({
      name: pricelist.name,
      description: pricelist.description || '',
      client_id: pricelist.client?.id?.toString() || '',
      project_id: pricelist.project?.id?.toString() || '',
      billable_rate: pricelist.billable_rate.toString(),
      cost_rate: pricelist.cost_rate.toString(),
      currency: pricelist.currency,
      effective_date: pricelist.effective_date,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const payload = {
        ...formData,
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        project_id: formData.project_id ? parseInt(formData.project_id) : null,
        billable_rate: parseFloat(formData.billable_rate),
        cost_rate: parseFloat(formData.cost_rate),
      };

      let createdPricelistId: number | null = null;

      if (editingPricelist) {
        await pricelistsAPI.update(editingPricelist.id, payload);
      } else {
        const response = await pricelistsAPI.create(payload);
        createdPricelistId = response.data.pricelist?.id;
      }

      await fetchData();
      resetForm();

      // If creating a new pricelist, show the detail view to add users
      if (createdPricelistId) {
        setSelectedPricelistId(createdPricelistId);
        setIsNewPricelist(true);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || t('pricelists.createFailed'));
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: t('pricelists.deleteTitle'),
      message: t('pricelists.deleteConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await pricelistsAPI.delete(id);
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.error || t('pricelists.deleteFailed'));
    }
  };

  const handleManageUsers = (pricelistId: number) => {
    setSelectedPricelistId(pricelistId);
    setIsNewPricelist(false);
  };

  const handleBackFromDetail = () => {
    setSelectedPricelistId(null);
    setIsNewPricelist(false);
    fetchData(); // Refresh to get updated user counts
  };

  // Get unique clients for filter (must be before early returns for React hooks rules)
  const uniqueClients = useMemo(() => {
    const clientMap = new Map<number, Client>();
    pricelists.forEach((p) => {
      if (p.client) {
        clientMap.set(p.client.id, p.client);
      }
    });
    return Array.from(clientMap.values());
  }, [pricelists]);

  // Filter pricelists (must be before early returns for React hooks rules)
  const filteredPricelists = useMemo(() => {
    return pricelists.filter((pricelist) => {
      const matchesSearch =
        !searchQuery ||
        pricelist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pricelist.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());
      const matchesClient =
        !clientFilter || pricelist.client?.id?.toString() === clientFilter;
      return matchesSearch && matchesClient;
    });
  }, [pricelists, searchQuery, clientFilter]);

  // Show PricelistDetailView when a pricelist is selected
  if (selectedPricelistId !== null) {
    return (
      <PricelistDetailView
        pricelistId={selectedPricelistId}
        onBack={handleBackFromDetail}
        isNewPricelist={isNewPricelist}
      />
    );
  }

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 rounded-2xl'
    : 'bg-white border border-slate-200 rounded-2xl shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent'
    : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500';

  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent'
    : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500';

  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-2'
    : 'block text-sm font-medium text-gray-700 mb-1';

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Sticky Header */}
      <div
        className={`sticky top-0 z-20 ${isDark ? 'bg-slate-900' : 'bg-slate-50'} pb-4 md:-mx-6  md:px-6 pt-0`}
      >
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 items-center justify-center text-white shadow-lg shadow-violet-500/25">
              {Icons.pricelist}
            </div>
            <div>
              <h1
                className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('pricelists.title')}
              </h1>
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {filteredPricelists.length}{' '}
                {t('pricelists.pricelists').toLowerCase()}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {t('pricelists.createNew')}
          </button>
        </div>

        {/* Search and Filters */}
        <div
          className={`p-4 rounded-2xl ${isDark ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'}`}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {Icons.search}
              </div>
              <input
                type="text"
                placeholder={t('pricelists.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 rounded-xl ${
                  isDark
                    ? 'bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                }`}
              />
            </div>
            {/* Client Filter */}
            <div className="w-full sm:w-48">
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl ${
                  isDark
                    ? 'bg-slate-700/50 border border-slate-600 text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    : 'bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                }`}
              >
                <option value="">{t('pricelists.allClients')}</option>
                {uniqueClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div
          className={`p-3 rounded-xl ${isDark ? 'bg-rose-500/10 border border-rose-500/30 text-rose-400' : 'bg-red-100 border border-red-400 text-red-700'}`}
        >
          {error}
        </div>
      )}

      {/* Pricelist Form */}
      {showForm && (
        <div className={`mb-6 p-6 ${cardClass}`}>
          <h3
            className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            {editingPricelist
              ? t('common.edit') + ' ' + t('pricelists.pricelists').slice(0, -1)
              : t('pricelists.createPricelist')}
          </h3>
          <p
            className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
          >
            {t('pricelists.createDescription')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className={labelClass}>{t('pricelists.name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={inputClass}
                  placeholder={t('pricelists.namePlaceholder')}
                  required
                />
              </div>

              {/* Client */}
              <div>
                <label className={labelClass}>{t('pricelists.client')}</label>
                <select
                  value={formData.client_id}
                  onChange={(e) =>
                    setFormData({ ...formData, client_id: e.target.value })
                  }
                  className={selectClass}
                >
                  <option value="">{t('pricelists.organizationWide')}</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} {client.company && `(${client.company})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project */}
              <div>
                <label className={labelClass}>{t('pricelists.project')}</label>
                <select
                  value={formData.project_id}
                  onChange={(e) =>
                    setFormData({ ...formData, project_id: e.target.value })
                  }
                  className={selectClass}
                >
                  <option value="">{t('pricelists.anyProject')}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                      {project.is_internal
                        ? ` (${t('common.internal')})`
                        : project.client
                          ? ` - ${project.client.name}`
                          : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Currency */}
              <div>
                <label className={labelClass}>
                  {t('pricelists.currency')} *
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      currency: e.target.value as any,
                    })
                  }
                  className={selectClass}
                  required
                >
                  <option value="CAD">{t('pricelists.currencyCAD')}</option>
                  <option value="USD">{t('pricelists.currencyUSD')}</option>
                </select>
              </div>

              {/* Billable Rate (Default) */}
              <div>
                <label className={labelClass}>
                  {t('pricelists.defaultBillableRate')} *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.billable_rate}
                  onChange={(e) =>
                    setFormData({ ...formData, billable_rate: e.target.value })
                  }
                  className={inputClass}
                  placeholder="0.00"
                  required
                />
                <p
                  className={`mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                >
                  {t('pricelists.defaultRateHint')}
                </p>
              </div>

              {/* Cost Rate (Default) */}
              <div>
                <label className={labelClass}>
                  {t('pricelists.defaultCostRate')} *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_rate}
                  onChange={(e) =>
                    setFormData({ ...formData, cost_rate: e.target.value })
                  }
                  className={inputClass}
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Effective Date */}
              <div>
                <label className={labelClass}>
                  {t('pricelists.effective')} *
                </label>
                <input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) =>
                    setFormData({ ...formData, effective_date: e.target.value })
                  }
                  className={inputClass}
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className={labelClass}>{t('common.description')}</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={2}
                className={inputClass}
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={resetForm}
                className={`px-4 py-2 rounded-xl transition-colors ${isDark ? 'border border-slate-600 text-slate-300 hover:bg-slate-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
              >
                {editingPricelist
                  ? t('common.update')
                  : t('pricelists.createAndAddUsers')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pricelists Table */}
      <div className={cardClass}>
        {pricelists.length === 0 ? (
          <div
            className={`p-8 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
          >
            {t('pricelists.noPricelists')}
          </div>
        ) : filteredPricelists.length === 0 ? (
          <div
            className={`p-12 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
              {Icons.search}
            </div>
            <p
              className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('pricelists.noResults')}
            </p>
            <p className="text-sm mt-1">
              {t('pricelists.tryDifferentFilters')}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div
              className={`md:hidden divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}
            >
              {filteredPricelists.map((pricelist: any) => (
                <div
                  key={pricelist.id}
                  className={`p-4 ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'}`}
                >
                  {/* Name & Users Badge */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
                      >
                        {pricelist.name}
                      </h3>
                      <p
                        className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                      >
                        {pricelist.scope_description}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-blue-100 text-blue-800'}`}
                    >
                      {pricelist.user_assignments?.length || 0}{' '}
                      {t('pricelists.users')}
                    </span>
                  </div>

                  {/* Description */}
                  {pricelist.description && (
                    <p
                      className={`text-xs truncate mb-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                    >
                      {pricelist.description}
                    </p>
                  )}

                  {/* Rates & Date */}
                  <div className="flex flex-wrap items-center gap-3 mb-3 text-xs">
                    <span className="text-emerald-500">
                      {t('pricelists.billable')}:{' '}
                      {pricelist.formatted_billable_rate}
                    </span>
                    <span className="text-amber-500">
                      {t('pricelists.cost')}: {pricelist.formatted_cost_rate}
                    </span>
                    <span
                      className={isDark ? 'text-slate-500' : 'text-gray-500'}
                    >
                      {new Date(pricelist.effective_date).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleManageUsers(pricelist.id)}
                      className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all"
                      title={t('pricelists.manageUsers')}
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEdit(pricelist)}
                      className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                      title={t('common.edit')}
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(pricelist.id)}
                      className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                      title={t('common.delete')}
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block overflow-x-auto">
              <table
                className={`min-w-full divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}
              >
                <thead className={isDark ? 'bg-slate-900/50' : 'bg-gray-50'}>
                  <tr>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      {t('pricelists.nameAndScope')}
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      {t('pricelists.usersAssigned')}
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      {t('pricelists.defaultRates')}
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      {t('pricelists.effective')}
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}
                >
                  {filteredPricelists.map((pricelist: any) => (
                    <tr
                      key={pricelist.id}
                      className={
                        isDark ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                      }
                    >
                      <td className="px-6 py-4">
                        <div
                          className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}
                        >
                          {pricelist.name}
                        </div>
                        <div
                          className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                        >
                          {pricelist.scope_description}
                        </div>
                        {pricelist.description && (
                          <div
                            className={`text-xs max-w-xs truncate ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                          >
                            {pricelist.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-blue-100 text-blue-800'}`}
                          >
                            {pricelist.user_assignments?.length || 0}{' '}
                            {t('pricelists.users')}
                          </span>
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-gray-900'}`}
                      >
                        <div className="text-emerald-500">
                          {t('pricelists.billable')}:{' '}
                          {pricelist.formatted_billable_rate}
                        </div>
                        <div className="text-amber-500">
                          {t('pricelists.cost')}:{' '}
                          {pricelist.formatted_cost_rate}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-gray-900'}`}
                      >
                        {new Date(
                          pricelist.effective_date,
                        ).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleManageUsers(pricelist.id)}
                            className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all"
                            title={t('pricelists.manageUsers')}
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEdit(pricelist)}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title={t('common.edit')}
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(pricelist.id)}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                            title={t('common.delete')}
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PricelistManagement;
